import React, { useState, useEffect } from 'react';
import { Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  AbilityEffect,
  AbilityLimitation, 
  CreatureGift, 
  DamageTypeOverrides 
} from "@dndModels";
import { DAMAGE_TYPES } from "@dndModels/Constants";
import { DamageType } from "@dndModels/Types";

import CommonUtilities from "@utilities/common_utilities";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import AbilityLimitInput from "@dndComponents/limitations/AbilityLimitInput";
import StringBox from "@components/input/strings/StringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import AbilityEffectInput from "@dndComponents/feature/AbilityEffectInput";

interface Props { 
  obj: CreatureGift;
  onChange: (changed: CreatureGift) => void;
  disabled: boolean;
}

const common_util = CommonUtilities.getInstance();

export default function DamageTypeOverridesInput({
  obj,
  onChange,
  disabled,
}: Props) {
  const [reloading, setReloading] = useState("");
  const [ping, setPing] = useState(false);
  const [change_time, setChangeTime] = useState(0);
  const [expanded, setExpanded] = useState("");
  const [show_effect, setShowEffect] = useState("");

  useEffect(() => {
    if (reloading !== "") {
      setReloading("");
    }
  }, [reloading, setReloading]);
  
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        <AddButton
          disabled={disabled}
          onClick={() => {
            const new_obj = obj;
            const entry = new DamageTypeOverrides();
            new_obj.damage_type_overrides_table.entries.push(entry);
            onChange(new_obj);
            setChangeTime(common_util.get_time_number());
            setExpanded(entry.true_id);
          }}
        />
        &nbsp;
        Damage Type Overrides
      </Grid>
      { reloading !== "Overrides" && obj.damage_type_overrides_table.entries.map((override_entry, key) => {
        return (
          <Grid
            key={key}
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              className="header clickable"
              onClick={() => {
                setExpanded(expanded === override_entry.true_id ? "" : override_entry.true_id);
              }}
              size={{
                xs: 8
              }}>
              {override_entry.name}
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <DeleteButton
                disabled={disabled}
                onClick={() => {
                  const new_obj = obj;
                  new_obj.damage_type_overrides_table.entries = new_obj.damage_type_overrides_table.entries.filter(o => o.true_id !== override_entry.true_id);
                  onChange(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setReloading("Overrides");
                }}
              />
            </Grid>
            { expanded === override_entry.true_id &&
              <>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <StringBox
                    name="Name"
                    value={override_entry.name}
                    disabled={disabled}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const entry = new_obj.damage_type_overrides_table.entries.find(o => o.true_id === override_entry.true_id);
                      if (entry) {
                        entry.name = changed;
                      }
                      onChange(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <SelectStringBox
                    name="From Damage Types"
                    options={DAMAGE_TYPES}
                    values={override_entry.from_damage_types}
                    disabled={disabled}
                    onChange={(changed: DamageType[]) => {
                      const new_obj = obj;
                      const entry = new_obj.damage_type_overrides_table.entries.find(o => o.true_id === override_entry.true_id);
                      if (entry) {
                        entry.from_damage_types = changed;
                      }
                      onChange(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <SelectStringBox
                    name="To Damage Type"
                    options={DAMAGE_TYPES}
                    value={override_entry.to_damage_type}
                    disabled={disabled}
                    onChange={(changed: DamageType) => {
                      const new_obj = obj;
                      const entry = new_obj.damage_type_overrides_table.entries.find(o => o.true_id === override_entry.true_id);
                      if (entry) {
                        entry.to_damage_type = changed;
                      }
                      onChange(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <StringBox
                    name="Description"
                    value={override_entry.description}
                    disabled={disabled}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const entry = new_obj.damage_type_overrides_table.entries.find(o => o.true_id === override_entry.true_id);
                      if (entry) {
                        entry.description = changed;
                      }
                      onChange(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <StringBox
                    name="Override Name with Formula"
                    value={override_entry.name_formula}
                    disabled={disabled}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const entry = new_obj.damage_type_overrides_table.entries.find(o => o.true_id === override_entry.true_id);
                      if (entry) {
                        entry.name_formula = changed;
                      }
                      onChange(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <AbilityLimitInput
                    obj={override_entry.ability_limit}
                    change_time={change_time}
                    disabled={disabled}
                    onChange={(changed: AbilityLimitation) => {
                      const new_obj = obj;
                      const entry = new_obj.damage_type_overrides_table.entries.find(o => o.true_id === override_entry.true_id);
                      if (entry) {
                        entry.ability_limit = changed;
                      }
                      onChange(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <span className="header">
                      Extra Effects
                    </span>
                    { !disabled &&
                      <Tooltip title={`Add Effect`}>
                        <AddButton
                          onClick={ () => {
                            const new_obj = obj;
                            const entry = new_obj.damage_type_overrides_table.entries.find(o => o.true_id === override_entry.true_id);
                            if (entry) {
                              const effect = new AbilityEffect();
                              entry.extra_effects.push(effect);
                              setShowEffect(effect.true_id);
                            }
                            onChange(new_obj);
                            setChangeTime(common_util.get_time_number());
                          }}
                        />
                      </Tooltip>
                    }
                  </Grid>
                  { reloading !== "Effects" && override_entry.extra_effects.map((effect, key) => {
                    let name = effect.type === "Damage" ? effect.damage_types.join(", ") : effect.type;
                    if (!["None","Self Condition"].includes(effect.type) && effect.attack_type !== "None") {
                      name += ` ${effect.attack_type}`
                    }

                    return (
                      <Grid
                        key={key}
                        container
                        spacing={0}
                        direction="row"
                        size={{
                          xs: 12
                        }}>
                        <Grid
                          size={{
                            xs: 12
                          }}>
                          <ToggleButtonBox
                            name={ name }
                            disabled={disabled}
                            value={ show_effect === effect.true_id }
                            onToggle={ () => { 
                              setShowEffect(show_effect === effect.true_id ? "" : effect.true_id);
                            }}
                          />
                        </Grid>
                        { show_effect === effect.true_id && 
                          <Grid
                            size={{
                              xs: 12
                            }}>
                            <AbilityEffectInput 
                              obj={effect}
                              disabled={disabled}
                              onChange={() => {
                                const new_obj = obj;
                                onChange(new_obj);
                                setPing(!ping);
                              }}
                              onDelete={() => {
                                const new_obj = obj;
                                const entry = new_obj.damage_type_overrides_table.entries.find(o => o.true_id === override_entry.true_id);
                                if (entry) {
                                  entry.extra_effects = entry.extra_effects.filter(o => o.true_id !== effect.true_id);
                                }
                                onChange(new_obj);
                                setChangeTime(common_util.get_time_number());
                                setReloading("Effects");
                              }}
                            />
                          </Grid>        
                        }
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            }
          </Grid>
        );
      })}
    </Grid>
  );
}
